import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import IconSvg from 'src/assets/icons/IconSvg';
import Button from 'src/components/button/Button';
import RaffleCountdown from 'src/components/common/RaffleCountdown';
import { useViewport } from 'src/context/ViewportContext';
import {
  getRaffleDisplayName,
  getRaffleImage,
  getRaffleCreator,
  satsToBtc,
} from 'src/utils/raffleHelpers';
import type { Raffle } from 'src/types/raffle';

interface Props {
  raffles: Raffle[];
  favoritedIds?: Set<string>;
  onToggleFavorite?: (raffleId: string) => void;
}

const RafflesTable = ({ raffles, favoritedIds, onToggleFavorite }: Props) => {
  const { isMobile } = useViewport();
  const navigate = useNavigate();

  return (
    <>
      {!isMobile && (
        <div className="hidden py-3 rounded-lg bg-grayscale-900 xl:block">
          <table className="w-full">
            <thead className="text-sm text-left text-grayscale-300">
              <tr>
                <th className="w-[348px] py-[13px] pl-[58px]">Raffle Name</th>

                <th className="w-[188px] py-[13px]">
                  <div className="flex gap-1 items-center">
                    <span>Ticket Price</span>
                    <IconSvg icon="trianglesS" />
                  </div>
                </th>

                <th className="w-[188px] py-[13px]">
                  <div className="flex gap-1 items-center">
                    <span>Remaining / Total Tickets</span>
                    <IconSvg icon="trianglesS" />
                  </div>
                </th>

                <th className="w-[188px] py-[13px]">Total Tickets Value</th>

                <th className="w-[188px] py-[13px]">
                  <div className="flex gap-1 items-center">
                    <span>Floor Price</span>
                    <IconSvg icon="trianglesS" />
                  </div>
                </th>

                <th className="w-[188px] py-[13px]">Pending Transactions</th>

                <th className="w-[188px] py-[13px]">
                  <div className="flex gap-1 items-center">
                    <span>Time Remaining</span>
                    <IconSvg icon="trianglesS" />
                  </div>
                </th>

                <th className="w-[100px] py-5"></th>
              </tr>
            </thead>

            <tbody>
              <tr style={{ height: '10px' }}></tr>

              {raffles.map(raffle => (
                <tr
                  className="text-lg rounded-lg transition-all duration-200 cursor-pointer text-grayscale-300 hover:bg-grayscale-700"
                  key={raffle.id}
                  onClick={() => navigate(`/raffle/${raffle.id}`)}
                >
                  <td className="py-3.5 pl-5 rounded-l-lg">
                    <div className="flex gap-3 items-center">
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          onToggleFavorite?.(raffle.id);
                        }}
                      >
                        <IconSvg
                          icon="starM"
                          className={favoritedIds?.has(raffle.id) ? 'text-brand-tangerine' : 'text-grayscale-500'}
                        />
                      </button>

                      <div className="flex gap-3 items-center">
                        <img
                          src={getRaffleImage(raffle)}
                          alt=""
                          className="flex-shrink-0 w-12 h-12 rounded-full object-cover"
                        />

                        <div className="space-y-1">
                          <div className="flex gap-1 items-center">
                            <div className="text-lg font-semibold text-brand-magnolia">
                              {getRaffleDisplayName(raffle)}
                            </div>

                            <IconSvg icon="verifiedS" className="text-brand-tangerine" />
                          </div>

                          <span className="text-xss text-grayscale-300">
                            Created by: {getRaffleCreator(raffle)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div>
                      <div className="flex gap-1 items-center text-grayscale-100">
                        <IconSvg icon="bitcoinS" />

                        <div className="text-lg font-semibold text-grayscale-100">
                          {satsToBtc(raffle.ticketPriceSats).toFixed(4)}
                        </div>
                      </div>

                      <div className="text-xs text-grayscale-400">
                        ≈ ${(satsToBtc(raffle.ticketPriceSats) * 97103).toFixed(2)}
                      </div>
                    </div>
                  </td>

                  <td>
                    {(raffle.supply - raffle.ticketsSold).toLocaleString()} /{' '}
                    {raffle.supply.toLocaleString()}
                  </td>

                  <td>
                    <div>
                      <div className="flex gap-1 items-center text-grayscale-100">
                        <IconSvg icon="bitcoinS" />

                        <div className="text-lg font-semibold text-grayscale-100">
                          {satsToBtc(raffle.totalTicketValue).toFixed(4)}
                        </div>
                      </div>

                      <div className="text-xs text-grayscale-400">
                        ≈ ${(satsToBtc(raffle.totalTicketValue) * 97103).toFixed(2)}
                      </div>
                    </div>
                  </td>

                  <td>
                    <div>
                      <div className="flex gap-1 items-center text-grayscale-100">
                        <IconSvg icon="bitcoinS" />

                        <div className="text-lg font-semibold text-grayscale-100">
                          {satsToBtc(raffle.ticketPriceSats).toFixed(4)}
                        </div>
                      </div>

                      <div className="text-xs text-grayscale-400">
                        ≈ ${(satsToBtc(raffle.ticketPriceSats) * 97103).toFixed(2)}
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className="flex gap-1 items-center text-lg font-semibold text-grayscale-100">
                      <IconSvg icon="progressS" />

                      <span>0</span>
                    </div>
                  </td>

                  <td>
                    <RaffleCountdown endDate={raffle.endDate} className="text-sm" />
                  </td>

                  <td className="pr-5 rounded-r-lg">
                    <div className="flex justify-end items-center">
                      {new Date() >= new Date(raffle.endDate) ? (
                        <Button
                          variant="primary"
                          size="sm"
                          className="w-[100px]"
                          disabled
                        >
                          Ended
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          className="w-[100px]"
                          onClick={e => {
                            e.stopPropagation();
                            navigate(`/raffle/${raffle.id}`);
                          }}
                        >
                          Buy
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isMobile && (
        <div className="grid gap-2.5">
          {raffles.map(raffle => (
            <Disclosure
              as="div"
              className="overflow-hidden relative p-4 rounded-lg bg-grayscale-800"
              key={raffle.id}
            >
              <button
                className="absolute top-0 left-0 w-[38px] h-[41px] overflow-hidden z-10"
                onClick={e => {
                  e.stopPropagation();
                  onToggleFavorite?.(raffle.id);
                }}
              >
                <div className="w-0 h-0 border-t-[41px] border-t-grayscale-700 border-r-[38px] border-r-transparent">
                  <IconSvg
                    icon="starM"
                    className={`absolute top-[3px] left-[3px] w-[18px] h-[18px] ${
                      favoritedIds?.has(raffle.id) ? 'text-brand-tangerine' : 'text-grayscale-500'
                    }`}
                  />
                </div>
              </button>

              <DisclosureButton className="flex relative justify-between items-center w-full rounded-lg group">
                <div className="flex gap-3 items-center">
                  <img
                    src={getRaffleImage(raffle)}
                    alt=""
                    className="flex-shrink-0 w-12 h-12 rounded-full object-cover"
                  />

                  <div className="space-y-1">
                    <div className="flex gap-1 items-center">
                      <div className="text-lg font-semibold text-brand-magnolia">
                        {getRaffleDisplayName(raffle)}
                      </div>

                      <IconSvg icon="verifiedS" className="text-brand-tangerine" />
                    </div>

                    <RaffleCountdown endDate={raffle.endDate} className="text-xss" />
                  </div>
                </div>

                <IconSvg
                  icon="arrowDownM"
                  className="text-grayscale-300 group-data-[open]:rotate-180 transition-all duration-200"
                />
              </DisclosureButton>

              <DisclosurePanel className="!mt-3 transition-all duration-200">
                <div className="flex justify-between items-start py-3 border-t border-grayscale-700">
                  <div>
                    <div className="text-xs text-grayscale-300">Tickets Price</div>

                    <div className="flex gap-1 items-center text-grayscale-100">
                      <IconSvg icon="bitcoinS" />

                      <div className="font-semibold text-grayscale-100">
                        {satsToBtc(raffle.ticketPriceSats).toFixed(4)}
                      </div>
                    </div>

                    <div className="text-xs text-grayscale-400">
                      ≈ ${(satsToBtc(raffle.ticketPriceSats) * 97103).toFixed(2)}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-xs text-grayscale-300">Remaining / Total Tickets</div>

                    <div className="font-semibold text-grayscale-100">
                      {(raffle.supply - raffle.ticketsSold).toLocaleString()} /{' '}
                      {raffle.supply.toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-start py-3 border-t border-grayscale-700">
                  <div>
                    <div className="text-xs text-grayscale-300">Total Tickets Value</div>

                    <div className="flex gap-1 items-center text-grayscale-100">
                      <IconSvg icon="bitcoinS" />

                      <div className="font-semibold text-grayscale-100">
                        {satsToBtc(raffle.totalTicketValue).toFixed(4)}
                      </div>
                    </div>

                    <div className="text-xs text-grayscale-400">
                      ≈ ${(satsToBtc(raffle.totalTicketValue) * 97103).toFixed(2)}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-xs text-grayscale-300">Floor/Market Price</div>

                    <div className="flex gap-1 justify-end items-center text-grayscale-100">
                      <IconSvg icon="bitcoinS" />

                      <div className="font-semibold text-grayscale-100">
                        {satsToBtc(raffle.ticketPriceSats).toFixed(4)}
                      </div>
                    </div>

                    <div className="text-xs text-grayscale-400">
                      ≈ ${(satsToBtc(raffle.ticketPriceSats) * 97103).toFixed(2)}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-start py-3 border-t border-grayscale-700">
                  <div>
                    <div className="text-xs text-grayscale-300">Pending Transactions</div>

                    <div className="flex gap-1 items-center font-semibold text-grayscale-100">
                      <IconSvg icon="progressS" />

                      <span>0</span>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-xs text-grayscale-300">Created by:</div>

                    <div className="font-semibold text-grayscale-100">
                      {getRaffleCreator(raffle)}
                    </div>
                  </div>
                </div>
              </DisclosurePanel>
            </Disclosure>
          ))}
        </div>
      )}
    </>
  );
};

export default RafflesTable;
